import { bboxOfPoly, pointInPoly } from '../../shared/geom.js';
import type { Area } from '../../shared/types.js';
import type { ObjectBuilder, ObjectContext } from './cityObjects.js';

/**
 * Painted bays in the car parks: a white line every two and a half metres either side of
 * each aisle, five metres deep. A supermarket's car park is the largest single thing in
 * front of it, and a plain grey polygon there read as a hole in the map.
 *
 * The aisles are what place the bays, because they are what OSM has: `service=parking_aisle`
 * ways down the middle of a lot, mapped far more often than the bays themselves. That tag is
 * not in the payload, so an aisle here is any service road that runs inside a car park,
 * which is the same thing by a test that also catches the ones nobody tagged. A lot with no
 * aisle gets no paint, which is better than a guess at its layout.
 */

const BAY_WIDTH = 2.5;
const BAY_DEPTH = 5;
const LINE_WIDTH = 0.12;
const PAINT = 0xd6d4cc;
/**
 * Above the car park's fill: over the 13 cm a fill may be lifted to where the terrain sags
 * under it (`FILL_CEILING_Y` in `worldMesh.ts`), and under the service roads, at 15.2, that the
 * bays line. The two are only a few millimetres apart in places, which is what
 * `ObjectBuilder.onGround` is for. Paint a few centimetres over a flat lot does not show.
 */
const PAINT_Y = 0.145;
/** On a flat world nothing is lifted, so the paint can lie on the tarmac. */
const PAINT_Y_FLAT = 0.05;

export function buildCarParks(out: ObjectBuilder, ctx: ObjectContext): void {
  const lots = ctx.world.areas.filter((a) => a.kind === 'parking');
  if (!lots.length) return;
  const boxes = lots.map((a) => bboxOfPoly(a.poly));
  const lotAt = (x: number, z: number): Area | null => {
    for (let i = 0; i < lots.length; i++) {
      const bb = boxes[i];
      if (x < bb.minX || x > bb.maxX || z < bb.minZ || z > bb.maxZ) continue;
      if (pointInPoly(lots[i].poly, x, z) && !lots[i].holes.some((h) => pointInPoly(h, x, z))) return lots[i];
    }
    return null;
  };

  const lift = ctx.terrain.isFlat ? PAINT_Y_FLAT : PAINT_Y;
  // Where two aisles meet at a corner of a lot, each one's bays reach into the other's and
  // the paint crossed in a lattice. The first aisle to claim the ground keeps it.
  const painted = new LineGrid();
  out.paint(PAINT);
  for (const road of ctx.world.roads) {
    if (road.cls !== 'service') continue;
    for (let i = 0; i + 3 < road.pts.length; i += 2) {
      const ax = road.pts[i], az = road.pts[i + 1];
      const bx = road.pts[i + 2], bz = road.pts[i + 3];
      const len = Math.hypot(bx - ax, bz - az);
      if (len < BAY_WIDTH * 2) continue;
      const tx = (bx - ax) / len;
      const tz = (bz - az) / len;
      const lot = lotAt((ax + bx) / 2, (az + bz) / 2);
      if (!lot) continue;
      // Whole bays, centred on the segment, clear of the junctions at its ends.
      const bays = Math.floor((len - 3) / BAY_WIDTH);
      const start = (len - bays * BAY_WIDTH) / 2;
      for (const side of [-1, 1]) {
        const nx = -tz * side;
        const nz = tx * side;
        const inner = road.width / 2 + 0.4;
        for (let k = 0; k <= bays; k++) {
          const s = start + k * BAY_WIDTH;
          const x0 = ax + tx * s + nx * inner;
          const z0 = az + tz * s + nz * inner;
          const x1 = x0 + nx * BAY_DEPTH;
          const z1 = z0 + nz * BAY_DEPTH;
          // All of the line in the same lot, on nobody's carriageway and against no wall.
          if (lotAt(x1, z1) !== lot || lotAt(x0, z0) !== lot) continue;
          if (ctx.surface.sample(x1, z1).onRoad || ctx.surface.sample((x0 + x1) / 2, (z0 + z1) / 2).onRoad) continue;
          if (ctx.buildings.resolveCircle(x1, z1, 0.3)) continue;
          if (painted.crosses(x0, z0, x1, z1)) continue;
          painted.add(x0, z0, x1, z1);
          out.onGround().stripe(
            x0, ctx.terrain.heightAt(x0, z0) + lift, z0,
            x1, ctx.terrain.heightAt(x1, z1) + lift, z1,
            LINE_WIDTH,
          );
        }
      }
    }
  }
}

/** Painted lines so far, gridded, for a crossing test. */
class LineGrid {
  private static readonly CELL = 8;
  private readonly cells = new Map<number, number[][]>();

  private keys(ax: number, az: number, bx: number, bz: number): number[] {
    const C = LineGrid.CELL;
    const out: number[] = [];
    for (let cx = Math.floor(Math.min(ax, bx) / C); cx <= Math.floor(Math.max(ax, bx) / C); cx++) {
      for (let cz = Math.floor(Math.min(az, bz) / C); cz <= Math.floor(Math.max(az, bz) / C); cz++) {
        out.push(cx * 100003 + cz);
      }
    }
    return out;
  }

  add(ax: number, az: number, bx: number, bz: number): void {
    for (const k of this.keys(ax, az, bx, bz)) {
      const list = this.cells.get(k);
      if (list) list.push([ax, az, bx, bz]);
      else this.cells.set(k, [[ax, az, bx, bz]]);
    }
  }

  crosses(ax: number, az: number, bx: number, bz: number): boolean {
    for (const k of this.keys(ax, az, bx, bz)) {
      for (const [cx, cz, dx, dz] of this.cells.get(k) ?? []) {
        if (segmentsCross(ax, az, bx, bz, cx, cz, dx, dz)) return true;
      }
    }
    return false;
  }
}

function segmentsCross(
  ax: number, az: number, bx: number, bz: number,
  cx: number, cz: number, dx: number, dz: number,
): boolean {
  const side = (px: number, pz: number, qx: number, qz: number, rx: number, rz: number) =>
    (qx - px) * (rz - pz) - (qz - pz) * (rx - px);
  const d1 = side(cx, cz, dx, dz, ax, az);
  const d2 = side(cx, cz, dx, dz, bx, bz);
  const d3 = side(ax, az, bx, bz, cx, cz);
  const d4 = side(ax, az, bx, bz, dx, dz);
  return d1 * d2 < 0 && d3 * d4 < 0;
}
